import { type FC, useEffect, useState } from "react";
import {
  ContactLink,
  ContactTitle,
  TgContactWrapper,
} from "widgets/profile/ui/styles.ts";
import Button from "@atomaro/ui-kit/components/Button/Button/Button";
import { TgIcon } from "shared/ui/icons";
import { Input, TextArea } from "@atomaro/ui-kit";
import { $api } from "shared/api";
import { useUserStore } from "entities/user";

type FieldErrors = {
  name?: string;
  email?: string;
  text?: string;
  common?: string;
};

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export const ContactContent: FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const user = useUserStore((s) => s.userInfo);

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!name && user) {
      const defName = user.name.first + " " + user.name.last;
      if (defName) {
        setName(defName);
      }
    }
  }, [user, name]);

  useEffect(() => {
    if (!email && user && user.credentials.email) {
      setEmail(user.credentials.email);
    }
  }, [user, email]);

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};

    const nameVal = String(name ?? "").trim();
    const emailVal = String(email ?? "").trim();
    const textVal = String(text ?? "").trim();

    if (!nameVal) next.name = "Укажите имя";
    else if (nameVal.length < 2) next.name = "Слишком короткое имя";

    if (!emailVal) next.email = "Укажите email";
    else if (!emailRe.test(emailVal)) next.email = "Некорректный email";

    if (!textVal) next.text = "Опишите ваш вопрос";
    else if (textVal.length < 10) next.text = "Минимум 10 символов";

    return next;
  };

  const onSubmit = async () => {
    const v = validate();
    setErrors(v);
    if (v.name || v.email || v.text) return;

    setLoading(true);
    setErrors({});
    try {
      await $api.post("/feedback", {
        name: String(name).trim(),
        email: String(email).trim(),
        text: String(text).trim(),
      });
      setSent(true);
      setName("");
      setEmail("");
      setText("");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Не удалось отправить сообщение";
      setErrors({ common: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TgContactWrapper style={isMobile ? { background: "transparent" } : {}}>
      <ContactTitle>Связаться с нами</ContactTitle>
      <Button
        label={"Написать нам"}
        variant={"primary"}
        size={"l"}
        iconPrefix={<TgIcon />}
        onClick={() => window.open("https://t.me/Hive_Technical_support_bot")}
      />

      <ContactTitle>Наши контакты</ContactTitle>
      <ContactLink href={"mailto:ecpp@rostelecom-rb.ru"}>
        ecpp@rostelecom-rb.ru
      </ContactLink>

      <ContactTitle>Задать вопрос</ContactTitle>

      <Input
        label={"Имя"}
        size={"l"}
        value={name}
        onChange={(e) => setName(String(e.target.value))}
        error={errors.name}
      />
      <Input
        label={"Email"}
        size={"l"}
        value={email}
        onChange={(e) => setEmail(String(e.target.value))}
        error={errors.email}
      />
      <TextArea
        label={"Ваш вопрос"}
        size={"l"}
        value={text}
        onChange={(e) => setText(String(e.target.value))}
        error={errors.text}
      />

      {!!errors.common && (
        <div
          style={{
            color: "var(--input-primary-hint-color-error, #ff2626)",
            marginBottom: 8,
          }}
        >
          {errors.common}
        </div>
      )}
      {sent && (
        <div style={{ color: "var(--success, #039855)", marginBottom: 8 }}>
          Сообщение отправлено. Спасибо!
        </div>
      )}

      <Button
        size={"l"}
        label={loading ? "Отправка..." : "Отправить"}
        disabled={loading}
        onClick={onSubmit}
      />
    </TgContactWrapper>
  );
};
